import { useState } from 'react';
import { GraduationCap, Utensils, Hospital, Trees, ShoppingBag, Bus, MapPin } from 'lucide-react';

const CATS = [
  { id: 'all', label: 'All', icon: MapPin },
  { id: 'school', label: 'Schools', icon: GraduationCap },
  { id: 'restaurant', label: 'Restaurants', icon: Utensils },
  { id: 'hospital', label: 'Hospitals', icon: Hospital },
  { id: 'park', label: 'Parks', icon: Trees },
  { id: 'mall', label: 'Malls', icon: ShoppingBag },
  { id: 'transport', label: 'Transport', icon: Bus },
];

interface Amenity {
  id: string;
  name: string;
  category: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export function ExploreAmenities({
  amenities,
  centerLat,
  centerLng,
  neighborhoodName,
}: {
  amenities: Amenity[];
  centerLat?: number | null;
  centerLng?: number | null;
  neighborhoodName: string;
}) {
  const [active, setActive] = useState('all');
  const filtered = active === 'all' ? amenities : amenities.filter(a => a.category === active);
  const mapSrc = centerLat && centerLng
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${centerLng - 0.02}%2C${centerLat - 0.02}%2C${centerLng + 0.02}%2C${centerLat + 0.02}&layer=mapnik&marker=${centerLat}%2C${centerLng}`
    : null;

  return (
    <section className="space-y-4">
      <h2 className="text-xl md:text-2xl font-bold">Explore {neighborhoodName}</h2>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {CATS.map(c => (
          <button
            key={c.id}
            onClick={() => setActive(c.id)}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
              active === c.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-muted'
            }`}
          >
            <c.icon className="h-3.5 w-3.5" />
            {c.label}
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 aspect-[16/10] rounded-lg overflow-hidden border bg-muted">
          {mapSrc ? (
            <iframe
              src={mapSrc}
              title={`Map of ${neighborhoodName}`}
              className="w-full h-full border-0"
              loading="lazy"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">No map available</div>
          )}
        </div>
        <div className="border rounded-lg divide-y max-h-[500px] overflow-y-auto">
          {filtered.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground text-center">No places in this category yet.</div>
          )}
          {filtered.map(a => (
            <div key={a.id} className="p-3 flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                {(() => {
                  const C = CATS.find(c => c.id === a.category)?.icon || MapPin;
                  return <C className="h-4 w-4" />;
                })()}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{a.name}</div>
                {a.address && <div className="text-xs text-muted-foreground truncate">{a.address}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
